import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { useAuth } from "../modules/auth/useAuth";
import { Button } from "@xfrontend/xui-react-plus";
import { isEntra, setActiveLicenseId, signOutClean } from "#/modules/auth/idp";

// Terminal page for an authenticated user whose GET /me returned 403 (no user record or no
// org assignment). They cannot reach any protected page, so offer a sign-out to switch
// account and tell them to contact IT for access.
const Wrap = styled.main`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--x-bg-main, #0E0A13);
  padding: 24px;
`;

const Card = styled.div`
  width: min(480px, 100%);
  background: var(--x-bg-500, #2d293a);
  border: 1px solid var(--x-purple-500, #494358);
  border-radius: 12px;
  padding: 32px;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 20px;
  font-weight: 700;
  color: var(--x-gray-200, #f6f6f7);
  margin: 0 0 12px;
`;

const Message = styled.p`
  font-size: 14px;
  color: var(--x-gray-300, #e9e9eb);
  margin: 0 0 6px;
`;

const Contact = styled.p`
  font-size: 13px;
  color: var(--x-gray-400, #cfd0d3);
  margin: 0 0 24px;
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
`;

const NoAccess = (): JSX.Element => {
  const { t } = useTranslation("errors");
  const auth = useAuth();

  const handleSignOut = (): void => {
    signOutClean(() => auth.removeUser());
  };

  // Switch Microsoft account without a full logout — the most likely fix when a user lands
  // here because they signed in with the wrong account. Reuses the header switch-account flow.
  const handleSwitchAccount = (): void => {
    setActiveLicenseId(null);
    auth.signinRedirect({ extraQueryParams: { prompt: "select_account" } });
  };

  return (
    <Wrap>
      <Card>
        <Title>{t("noAccess.title")}</Title>
        <Message>{t("noAccess.message")}</Message>
        <Contact>{t("noAccess.contact")}</Contact>
        <Actions>
          {isEntra && (
            <Button variant="primary" onClick={handleSwitchAccount}>
              {t("noAccess.switchAccount")}
            </Button>
          )}
          <Button variant="secondary" onClick={handleSignOut}>
            {t("noAccess.signOut")}
          </Button>
        </Actions>
      </Card>
    </Wrap>
  );
};

export default NoAccess;
